/**
 * RoutineCard Component
 * 
 * Displays a routine with timeline-style task list.
 * Shows routine header, task timeline, and completion status.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { TaskItem } from './TaskItem';
import styles from './RoutineCard.module.scss';

// Generic type for flexibility
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyRoutine = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyTask = any;

interface RoutineCardProps {
  routine: AnyRoutine;
  onTaskEdit?: (taskId: string) => void;
  onAddTask?: () => void;
}

// Helper to get task name
const getTaskName = (task: AnyTask): string => 
  task.label || task.name || 'Untitled Task';

// Helper to get start time
const getStartTime = (task: AnyTask): string => 
  task.start_time || task.start || '00:00';

// Helper to get end time
const getEndTime = (task: AnyTask): string => 
  task.end_time || task.end || '00:00';

export const RoutineCard: React.FC<RoutineCardProps> = ({
  routine,
  onTaskEdit,
  onAddTask,
}) => {
  const sortedTasks = [...(routine.tasks || [])].sort((a: AnyTask, b: AnyTask) => {
    return getStartTime(a).localeCompare(getStartTime(b));
  });

  return (
    <motion.div 
      className={styles.card}
      whileHover={{ scale: 1.01 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
    >
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {routine.icon && (
            <span className={styles.icon}>{routine.icon}</span>
          )}
          <h3 className={styles.name}>{routine.name}</h3>
        </div>
        {/* TODO: Add participant avatars */}
      </div>

      {/* Task list */}
      <div className={styles.taskList}>
        {sortedTasks.map((task, index) => (
          <TaskItem
            key={task.id}
            task={{
              id: task.id,
              name: getTaskName(task),
              startTime: getStartTime(task),
              endTime: getEndTime(task),
              icon: task.icon || undefined,
            }}
            onEdit={() => onTaskEdit?.(task.id)}
            isFirst={index === 0}
            isLast={index === sortedTasks.length - 1}
          />
        ))}
      </div>

      {/* Add task button */}
      <button 
        className={styles.addTaskButton}
        onClick={onAddTask}
        type="button"
      >
        + new Task
      </button>
    </motion.div>
  );
};

export default RoutineCard;
